import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { RetainerListItem } from '@goyone/shared';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { EmptyState, ErrorState, Notice, PageHead } from '../../components/ui';
import { Avatar } from '../../components/People';
import { useState } from 'react';
import { ClientPreviewModal } from './Previews';

/**
 * باقات العملاء — عدّادُ ما تعهّدنا به شهريًّا، وما سلّمناه منه.
 *
 * ## ما هذه الشاشة بالضبط
 *
 * عميلٌ يدفع شهريًّا مقابل كمٍّ متّفقٍ عليه من الخدمات: «١٢ تصميمًا و٤
 * فيديوهات و٨ منشورات كلَّ شهر». هذا هو الاشتراك (Retainer)، وكميّاتُه
 * تأتي من الباقة في كتالوج المبيعات.
 *
 * والاستهلاكُ **يُحسب من العمل نفسه** لا يُكتب بيد: كلُّ مهمّةٍ في مشاريع
 * العميل تُربط بخدمةٍ من الكتالوج وعددِ وحداتها، فإذا اكتملت خُصمت من
 * مسموح الشهر. فلا عدّادٌ يُحدَّث يدويًّا فيكذب — الشريطُ هنا هو المهامُّ
 * المكتملةُ نفسُها معدودةً.
 *
 * ## لماذا تحتاجها
 *
 * سؤالان يُسألان آخرَ الشهر ولا جواب لهما بلا هذه الصفحة: هل سلّمنا ما
 * تعاقدنا عليه (فلا يشكو العميل)، وهل سلّمنا أكثرَ منه (فنعمل مجّانًا).
 * والتجاوزُ يُعرف قبل الفوترة لا بعدها.
 *
 * والدورةُ شهريةٌ مرساةٌ على يوم الاشتراك: من بدأ في العاشر تُغلق دورتُه
 * في العاشر لا في أوّل الشهر الميلاديّ اعتباطًا.
 */
export function AgencyRetainers() {
  const [previewClient, setPreviewClient] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['agency', 'retainers-list'],
    queryFn: () => api<RetainerListItem[]>('/agency/retainers'),
  });

  const rows = list.data ?? [];
  const over = rows.filter((r) => r.items.some((i) => i.over > 0));
  /* من قارب المسموح ولم يتجاوزه — تحذيرُ ما قبل الوقوع. */
  const near = rows.filter(
    (r) =>
      !r.items.some((i) => i.over > 0) &&
      r.items.some((i) => i.allowance > 0 && i.used / i.allowance >= 0.8),
  );

  return (
    <div className="page">
      <PageHead
        title="باقات العملاء"
        subtitle="ما تعهّدنا بتسليمه شهريًّا لكل عميل، وكم سلّمنا منه فعلًا"
      />

      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {/*
        شرحُ الدور في الشاشة لا في دليلٍ منفصل.
        من يفتحها أوّل مرّةٍ يرى أشرطةً ولا يعرف من أين جاءت أرقامُها.
      */}
      {list.data && (
        <Notice kind="info" icon="info" title="كيف تُقرأ هذه الصفحة">
          الباقةُ اشتراكٌ شهريّ: كمٌّ متّفقٌ عليه من كلّ خدمة. والاستهلاكُ محسوبٌ من مهامّ
          مشاريع العميل — كلُّ مهمّةٍ مربوطةٍ بخدمةٍ وعددِ وحدات، إذا اكتملت خُصمت من مسموح
          الدورة. فالشريطُ ليس رقمًا يُكتب بل عملًا سُلّم.{' '}
          <Link to="/sales/packages">الباقات</Link> تُعرَّف في كتالوج المبيعات، والاشتراك
          يُضبط من ملفّ العميل.
        </Notice>
      )}

      {list.data && rows.length > 0 && (
        <div className="ag-stats">
          <span className="ag-stat">
            <Icon name="layers" size={18} />
            <strong className="num">{rows.length}</strong>
            <span>اشتراكًا جاريًا</span>
          </span>
          <span className={`ag-stat${over.length > 0 ? ' is-alert' : ''}`}>
            <Icon name="alert" size={18} />
            <strong className="num">{over.length}</strong>
            <span>تجاوز مسموحه</span>
          </span>
          <span className="ag-stat">
            <Icon name="clock" size={18} />
            <strong className="num">{near.length}</strong>
            <span>قارب المسموح</span>
          </span>
        </div>
      )}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا اشتراكات جارية"
          text="الباقةُ تعهّدٌ شهريّ بكمٍّ من الخدمات. عرّفها في كتالوج المبيعات، ثمّ اشترك لعميلٍ فيها من ملفّه (تبويب مهام الوكالة) — فيظهر هنا كم سلّمتَ من مسموحه."
          action={
            <Link to="/sales/packages" className="btn btn-primary">
              كتالوج الباقات
            </Link>
          }
        />
      )}

      <div className="ag-onb-grid">
        {rows.map((r) => {
          const hasOver = r.items.some((i) => i.over > 0);
          return (
            <div key={r.id} className={`ag-onb-card${hasOver ? ' is-over' : ''}`}>
              <div className="ag-onb-head">
                <Avatar name={r.accountName} size={36} />
                <div className="ag-onb-who">
                  <button className="ag-onb-name ag-linklike" onClick={() => setPreviewClient(r.accountId)}>
                    {r.accountName}
                  </button>
                  <span className="tiny mute">
                    {r.packageName} · الدورة {date(r.cycleStart)} — {date(r.cycleEnd)}
                  </span>
                </div>
                {hasOver && (
                  <span className="ag-chip ag-chip-late">
                    <Icon name="alert" size={11} />
                    تجاوز
                  </span>
                )}
              </div>

              {r.items.map((it) => {
                const pct = it.allowance
                  ? Math.min(100, Math.round((it.used / it.allowance) * 100))
                  : 0;
                return (
                  <div key={it.serviceId} className="ag-usage">
                    <div className="ag-usage-head">
                      <span className="small">{it.serviceName}</span>
                      <span className={`tiny num${it.over > 0 ? ' ag-late-text' : ' mute'}`}>
                        {it.used}/{it.allowance} {it.unit}
                        {it.over > 0 && ` — تجاوز ${it.over}`}
                      </span>
                    </div>
                    <div className={`ag-progress${it.over > 0 ? ' is-over' : ''}`}>
                      <span className="ag-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}

              {r.outsideUnits > 0 && (
                <p className="tiny mute">+ {r.outsideUnits} وحدة خارج الباقة هذه الدورة.</p>
              )}
            </div>
          );
        })}
      </div>
      {previewClient && (
        <ClientPreviewModal accountId={previewClient} onClose={() => setPreviewClient(null)} />
      )}
    </div>
  );
}
